import {
    useContext, useMemo, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from '@s20/hooks/redux.hooks';
import { getTenantSettings } from '@s20/selectors/features/common.selectors';
import { httpClient } from '@s20/services/httpClient';
import { YesNoButtons } from '../../../common/forms/YesNoButtons';
import SlideForm from '../../../common/forms/SlideForm';
import Input from '../../../common/forms/Input';
import { ApiRoutes } from '../../../Common';
import { FormContext, toasty } from '../../../common/forms/FormElements';
import ValidatedSelect from '../../../common/forms/ValidatedSelect';

export const ContractForm = ({ show, setShowSlideForm }) => {
    const queryClient = useQueryClient();
    const ref = useRef(null);

    const { setFieldValue, values: contextValues } = useContext(FormContext);
    const {
        companyId, dispatchOfficeLocationId, isIntercompanyCustomer, isQuickRental,
    } = contextValues;

    const tenantSettings = useSelector(getTenantSettings);
    const quickRentalTemplateId = tenantSettings?.quickRentalContractTemplateId;

    const { data: contractTemplates, isLoading } = useQuery({
        queryKey: ['flaggingContractTemplates'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.flaggingContractTemplates()),
    });

    const { data: customers } = useQuery({
        queryKey: ['customers'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.customers()),
    });

    const contractInitialValues = useMemo(() => {
        if (!isQuickRental) {
            return { prevailingWage: false, doNotRequireEndCompany: false };
        }
        const customerName = customers?.find((c) => c.value === companyId)?.label ?? '';
        return {
            prevailingWage: false,
            doNotRequireEndCompany: false,
            contractTemplateId: quickRentalTemplateId,
            description: customerName && `${customerName} Quick Rental`,
        };
    }, [isQuickRental, quickRentalTemplateId, customers, companyId]);

    const mutation = useMutation({
        mutationFn: async (values) => {
            const payload = { ...values };
            if (isQuickRental && payload.number && !payload.number.startsWith('QR-')) {
                payload.number = `QR-${payload.number.replace(/^QR/, '')}`;
            }
            const response = await httpClient.post(ApiRoutes.contract.createQuickContract(), {
                ...payload,
                companyId,
                dispatchLocations: [dispatchOfficeLocationId],
            });

            return response;
        },
        onSuccess: (contractId) => {
            queryClient.invalidateQueries({ queryKey: ['contractsForCustomer', companyId, dispatchOfficeLocationId] });
            queryClient.invalidateQueries({ queryKey: ['quickRentalContractsForCustomer', companyId, dispatchOfficeLocationId] });

            setFieldValue('contractId', contractId);
            setShowSlideForm(false);

            toasty.success('Contract added successfully');
        },
    });

    return createPortal(
        <SlideForm
            show={show}
            id="quickContractForm"
            formTitle={isQuickRental ? 'Add Quick Rental Contract' : 'Add Contract'}
            onSubmit={mutation.mutateAsync}
            onClose={() => setShowSlideForm(false)}
            formRef={ref}
            initialValues={contractInitialValues}
        >
            {({ values }) => (
                <>
                    <Input
                        label="Contract Number"
                        id="number"
                        name="number"
                        placeholder="Enter Number (letter and digits only)"
                        maxLength="150"
                        pattern="[a-zA-Z0-9\-]+"
                        required
                        error="Number is required."
                        startAddon={isQuickRental ? (
                            <span className="text-muted">QR-</span>
                        ) : undefined}
                    />

                    <Input
                        label="Description"
                        id="description"
                        name="description"
                        placeholder="Enter Description (max 500 characters)"
                        maxLength="500"
                        required
                        error="Please enter a description"
                    />

                    <ValidatedSelect
                        id="contractTemplateId"
                        name="contractTemplateId"
                        label="Template"
                        options={contractTemplates}
                        isLoading={isLoading}
                        required
                        hideClear
                        isDisabled={isQuickRental}
                        error="Template selection is required."
                    />

                    {!!isIntercompanyCustomer && (
                        <>
                            <YesNoButtons
                                label="Does not require an End Customer"
                                id="doNotRequireEndCompany"
                                name="doNotRequireEndCompany"
                                required
                            />
                            {!values.doNotRequireEndCompany && (
                                <ValidatedSelect
                                    id="endCompanyId"
                                    name="endCompanyId"
                                    label="End Customer"
                                    options={customers}
                                    required
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </SlideForm>,
        document.body,
    );
};